import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Switch, 
  FormControlLabel,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  AccountCircle,
  Notifications,
  Palette,
  LockReset
} from '@mui/icons-material';
import { auth } from '../utils/auth';

function Settings() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Get current user
        const currentUser = await auth.getCurrentUser();
        if (currentUser) {
          setEmail(currentUser.email || '');
        }
        
        // Load saved preferences from localStorage
        const savedDarkMode = localStorage.getItem('darkMode');
        if (savedDarkMode !== null) {
          setDarkMode(savedDarkMode === 'true');
        } else {
          // Use system preference if no saved preference
          setDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
        }
        
        const savedNotifications = localStorage.getItem('notificationsEnabled') !== 'false';
        setNotificationsEnabled(savedNotifications);
        
        const savedEmailNotifications = localStorage.getItem('emailNotifications') === 'true';
        setEmailNotifications(savedEmailNotifications);
        
        const savedPushNotifications = localStorage.getItem('pushNotifications') !== 'false';
        setPushNotifications(savedPushNotifications);
        
        const savedUsername = localStorage.getItem('username') || '';
        setUsername(savedUsername);
      } catch (err) {
        console.error('Error loading user data:', err);
        setError('Failed to load user data. Please try again.');
      }
    };

    loadUserData();
  }, [navigate]);

  const handleDarkModeToggle = (event) => {
    const isDarkMode = event.target.checked;
    setDarkMode(isDarkMode);
    localStorage.setItem('darkMode', isDarkMode);
    window.location.reload();
  };

  const handleNotificationsToggle = (event) => {
    setNotificationsEnabled(event.target.checked);
    localStorage.setItem('notificationsEnabled', event.target.checked);
  };

  const handleSaveChanges = async () => {
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Save settings to localStorage
      localStorage.setItem('username', username);
      localStorage.setItem('notificationsEnabled', notificationsEnabled.toString());
      localStorage.setItem('emailNotifications', emailNotifications.toString());
      localStorage.setItem('pushNotifications', pushNotifications.toString());
      
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    // Validate password inputs
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }
    
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // First verify current password by trying to log in
      await auth.login(email, currentPassword);
      
      // If login successful, update password
      await auth.updatePassword(newPassword);
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setSuccess('Password updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      if (err.message.includes('Invalid login credentials')) {
        setError('Current password is incorrect');
      } else {
        setError(err.message || 'Failed to update password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.logout();
      navigate('/login');
    } catch (error) {
      setError('Failed to log out');
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Settings
        </Typography>
        
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccountCircle />
                <Typography variant="h6">Account Settings</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                label="Username"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                value={email}
                disabled
                sx={{ mb: 2 }}
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleLogout}
                  sx={{ mr: 2 }}
                >
                  Logout
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
        
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LockReset />
                <Typography variant="h6">Password Settings</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                label="Current Password"
                variant="outlined"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="New Password"
                variant="outlined"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                variant="outlined"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button 
                variant="contained" 
                onClick={handlePasswordChange}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </AccordionDetails>
          </Accordion>
        </Paper>
        
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Palette />
                <Typography variant="h6">Appearance</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={<Switch checked={darkMode} onChange={handleDarkModeToggle} />}
                label="Dark Mode"
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                Changes to theme will reload the page to apply instantly
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Paper>
        
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Notifications />
                <Typography variant="h6">Notifications</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={<Switch checked={notificationsEnabled} onChange={handleNotificationsToggle} />}
                label="Enable Notifications"
                sx={{ mb: 2 }}
              />
              {notificationsEnabled && (
                <List>
                  <ListItem>
                    <FormControlLabel
                      control={<Switch checked={emailNotifications} onChange={(e) => setEmailNotifications(e.target.checked)} />}
                      label="Email Notifications"
                    />
                  </ListItem>
                  <ListItem>
                    <FormControlLabel
                      control={<Switch checked={pushNotifications} onChange={(e) => setPushNotifications(e.target.checked)} />}
                      label="Push Notifications"
                    />
                  </ListItem>
                </List>
              )}
            </AccordionDetails>
          </Accordion>
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSaveChanges}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </Box>
    </Container>
  );
}

export default Settings;